import PropTypes from 'prop-types';
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
// @mui
import { styled, useTheme, alpha } from '@mui/material/styles';
import { Box, Stack, Drawer, Typography } from '@mui/material';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useCollapseDrawer from '../../../hooks/useCollapseDrawer';
// utils
import cssStyles from '../../../utils/cssStyles';
// config
import { NAVBAR } from '../../../config';
// components
import Logo from '../../../components/Logo';
import Iconify from '../../../components/Iconify';
import Scrollbar from '../../../components/Scrollbar';
import { NavSectionVertical } from '../../../components/nav-section';

// routes
import { PATH_DASHBOARD } from '../../../routes/paths';
// components
// import Label from '../../../components/Label';
//

import NavbarDocs from './NavbarDocs';
import NavbarAccount from './NavbarAccount';
import CollapseButton from './CollapseButton';

// ----------------------------------------------------------------------

const RootStyle = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.shorter,
    }),
  },
}));

// ----------------------------------------------------------------------

NavbarVertical.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};

export default function NavbarVertical({ isOpenSidebar, onCloseSidebar }) {
  const theme = useTheme();
  const { pathname } = useLocation();

  const isDesktop = useResponsive('up', 'lg');

  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();
  // Iconify-based nav icons (matches the rest of the dashboard) with per-tone
  // colour so the rail reads at a glance even when collapsed.
  const navIcon = (icon, tint) => (
    <Box
      sx={{
        width: 32,
        height: 32,
        borderRadius: 1.25,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: alpha(tint, 0.12),
        color: tint,
      }}
    >
      <Iconify icon={icon} width={18} />
    </Box>
  );

  // Small "Soon" badge for services that aren't live yet.
  const soonChip = () => (
    <Box
      sx={{
        px: 0.85, py: 0.15,
        borderRadius: 999,
        bgcolor: alpha(theme.palette.warning.main, 0.16),
        color: 'warning.dark',
        fontSize: '0.6rem',
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: 'uppercase',
      }}
    >
      Soon
    </Box>
  );

  // Brand tints — kept in sync with the dashboard service tiles so the same
  // colour follows a service wherever it appears.
  const T = {
    blue:    theme.palette.primary.main,
    emerald: '#047857',
    amber:   '#b45309',
    violet:  '#6d28d9',
    indigo:  '#4338ca',
    rose:    '#be123c',
    pink:    '#be185d',
    cyan:    '#0369a1',
    slate:   '#475569',
  };

  // nav bar
  const navConfig = [
    {
      subheader: 'Main',
      items: [
        { title: 'Dashboard',    path: PATH_DASHBOARD.general.app,          icon: navIcon('eva:home-fill',         T.blue) },
        { title: 'Send to User', path: PATH_DASHBOARD.general.transferuser, icon: navIcon('eva:paper-plane-fill',  T.emerald) },
        { title: 'Withdraw',     path: PATH_DASHBOARD.general.withdraw,     icon: navIcon('eva:arrow-downward-fill', T.amber) },
        { title: 'Virtual Cards', path: PATH_DASHBOARD.general.cards,        icon: navIcon('eva:credit-card-fill',    T.indigo) },
        {
          title: 'Fund Wallet',
          path: PATH_DASHBOARD.fund.root,
          icon: navIcon('eva:plus-circle-fill', T.blue),
          children: [
            { title: 'Automated bank transfer', path: PATH_DASHBOARD.fund.account },
            { title: 'Dynamic funding',         path: PATH_DASHBOARD.fund.link_dynamic_account },
          ],
        },
      ],
    },
    {
      subheader: 'Pay & Recharge',
      items: [
        { title: 'Buy Data',        path: PATH_DASHBOARD.general.buydata,    icon: navIcon('eva:wifi-fill',           T.blue) },
        { title: 'Buy Airtime',     path: PATH_DASHBOARD.general.buyairtime, icon: navIcon('eva:phone-fill',          T.rose) },
        { title: 'Airtime to Cash', path: PATH_DASHBOARD.general.cash,       icon: navIcon('eva:swap-fill',           T.emerald) },
        { title: 'Electricity',     path: PATH_DASHBOARD.general.buybill,    icon: navIcon('eva:flash-fill',          T.amber) },
        { title: 'Cable TV',        path: PATH_DASHBOARD.general.buycable,   icon: navIcon('eva:tv-fill',             T.violet) },
        { title: 'Bulk SMS',        path: PATH_DASHBOARD.general.bulksms,    icon: navIcon('eva:message-circle-fill', T.indigo) },
        { title: 'Education',       path: PATH_DASHBOARD.general.exam,       icon: navIcon('mdi:school',              T.cyan) },
      ],
    },
    {
      subheader: 'International & Stores',
      items: [
        { title: 'eSIM',         path: PATH_DASHBOARD.general.esim,    icon: navIcon('eva:globe-fill',       T.cyan) },
        { title: 'Flight',       path: PATH_DASHBOARD.general.flight,  icon: navIcon('eva:paper-plane-fill', T.indigo) },
        { title: 'Gift Cards',   path: PATH_DASHBOARD.general.giftcard, icon: navIcon('eva:gift-fill',       T.pink) },
        { title: 'Smile',        path: PATH_DASHBOARD.general.smile,   icon: navIcon('eva:wifi-fill',        T.violet) },
        { title: 'Alpha',        path: PATH_DASHBOARD.general.alpha,   icon: navIcon('eva:phone-call-fill',  T.cyan) },
      ],
    },
    {
      subheader: 'Print & Bonus',
      items: [
        { title: 'Data Card',       path: PATH_DASHBOARD.general.data_card,     icon: navIcon('eva:credit-card-fill', T.violet) },
        { title: 'Recharge Card',   path: PATH_DASHBOARD.general.recharge_card, icon: navIcon('eva:hash-fill',        T.indigo) },
        { title: 'Bonus Transfer',  path: PATH_DASHBOARD.general.earning,       icon: navIcon('eva:gift-fill',        T.pink) },
      ],
    },
    {
      subheader: 'Transactions',
      items: [
        {
          title: 'History',
          path: PATH_DASHBOARD.trans.root,
          icon: navIcon('eva:clock-fill', T.slate),
          children: [
            { title: 'All transactions',  path: PATH_DASHBOARD.trans.history },
            { title: 'Deposits',          path: PATH_DASHBOARD.trans.deposit },
            { title: 'Manual funding',    path: PATH_DASHBOARD.trans.manualfunding },
            { title: 'Data',              path: PATH_DASHBOARD.trans.data },
            { title: 'Airtime',           path: PATH_DASHBOARD.trans.airtime },
            { title: 'Cable',             path: PATH_DASHBOARD.trans.cable },
            { title: 'Electricity',       path: PATH_DASHBOARD.trans.bill },
            { title: 'Bulk SMS',          path: PATH_DASHBOARD.trans.bulksms },
            { title: 'Airtime to Cash',   path: PATH_DASHBOARD.trans.airtimecash },
            { title: 'Education',         path: PATH_DASHBOARD.trans.result },
            { title: 'Data Card',         path: PATH_DASHBOARD.trans.data_card },
            { title: 'Recharge Card',     path: PATH_DASHBOARD.trans.recharge_card },
            { title: 'Withdrawals',       path: PATH_DASHBOARD.trans.withdraw },
            { title: 'Smile · Alpha · Kirani', path: PATH_DASHBOARD.trans.sak },
            { title: 'Smile · Alpha · Kirani eSIM', path: PATH_DASHBOARD.trans.number_esim },
            { title: 'eSIM',              path: PATH_DASHBOARD.trans.esim },
            { title: 'Gift cards',        path: PATH_DASHBOARD.trans.giftcard },
            { title: 'Gift card exchange', path: PATH_DASHBOARD.trans.giftcard_exchange },
          ],
        },
      ],
    },
    {
      subheader: 'Account',
      items: [
        { title: 'Verify tier',     path: PATH_DASHBOARD.fund.link_bvn,    icon: navIcon('eva:shield-fill',       T.emerald) },
        { title: 'Profile & settings', path: PATH_DASHBOARD.user.account, icon: navIcon('eva:person-fill',      T.slate) },
        { title: 'Pricing & API',   path: PATH_DASHBOARD.general.pricing,   icon: navIcon('eva:pricetags-fill',   T.indigo) },
        { title: 'Calculator',      path: PATH_DASHBOARD.general.calculator, icon: navIcon('solar:calculator-bold', T.slate) },
      ],
    },
  ];

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  const renderContent = (
    <Scrollbar
      sx={{
        height: 1,
        '& .simplebar-content': { height: 1, display: 'flex', flexDirection: 'column' },
      }}
    >
      <Stack
        spacing={3}
        sx={{
          pt: 3,
          pb: 2,
          px: 2.5,
          flexShrink: 0,
          ...(isCollapse && { alignItems: 'center' }),
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          {/* Logo + wordmark */}
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Logo disabledLink sx={{ width: 40, height: 40 }} />
            {!isCollapse && (
              <Typography
                variant="subtitle1"
                sx={{ fontWeight: 800, letterSpacing: '-0.5px', fontSize: '1.15rem' }}
              >
                <Box component="span" sx={{ color: 'primary.main' }}>Data</Box>
                <Box component="span" sx={{ color: 'secondary.main' }}>World</Box>
              </Typography>
            )}
          </Stack>

          {isDesktop && !isCollapse && (
            <CollapseButton onToggleCollapse={onToggleCollapse} collapseClick={collapseClick} />
          )}
        </Stack>

        {/* User info card modernized */}
        <NavbarAccount isCollapse={isCollapse} sx={{
          borderRadius: 2,
          boxShadow: 2,
          background: (theme) => theme.palette.background.paper,
          p: 2,
          mb: 1,
        }} />
      </Stack>
      {/* NavSectionVertical and rest unchanged, but you can add hover/active effects in nav-section if desired */}
      <NavSectionVertical navConfig={navConfig} isCollapse={isCollapse} />
      <Box sx={{ flexGrow: 1 }} />
      {!isCollapse && <NavbarDocs />}
    </Scrollbar>
  );

  return (
    <RootStyle
      sx={{
        width: {
          lg: isCollapse ? NAVBAR.DASHBOARD_COLLAPSE_WIDTH : NAVBAR.DASHBOARD_WIDTH,
        },
        ...(collapseClick && {
          position: 'absolute',
        }),
        background: (theme) => theme.palette.background.paper,
      }}
    >
      {!isDesktop && (
        <Drawer open={isOpenSidebar} onClose={onCloseSidebar} PaperProps={{ sx: { width: NAVBAR.DASHBOARD_WIDTH } }}>
          {renderContent}
        </Drawer>
      )}

      {isDesktop && (
        <Drawer
          open
          variant="persistent"
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
          PaperProps={{
            sx: {
              width: NAVBAR.DASHBOARD_WIDTH,
              borderRightStyle: 'solid',
              borderRightWidth: '1px',
              borderRightColor: 'divider',
              bgcolor: 'background.paper',
              transition: (theme) =>
                theme.transitions.create('width', {
                  duration: theme.transitions.duration.standard,
                }),
              ...(isCollapse && {
                width: NAVBAR.DASHBOARD_COLLAPSE_WIDTH,
              }),
              ...(collapseHover && {
                ...cssStyles(theme).bgBlur(),
                boxShadow: (theme) => theme.customShadows.z24,
              }),
            },
          }}
        >
          {renderContent}
        </Drawer>
      )}
    </RootStyle>
  );
}
